// services/enterprise.js
//import axios from 'axios';
import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';

/**
 * Create a new enterprise account
 * @param {Object} enterpriseData - Enterprise details
 * @returns {Promise<Object>} - Response with enterprise ID and key
 */
export const createEnterprise = async (enterpriseData) => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.post('/api/v1/enterprise', {
      userId,
      name: enterpriseData.name,
      seatLimit: enterpriseData.seatLimit,
      expirationDays: enterpriseData.expirationDays || 360
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating enterprise:', error);
    throw error;
  }
};

/**
 * Get enterprise details (admin only)
 * @param {string} enterpriseId - Enterprise ID
 * @returns {Promise<Object>} - Enterprise details
 */
export const getEnterpriseDetails = async (enterpriseId) => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}?userId=${userId}`);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching enterprise details:', error);
    throw error;
  }
};

/**
 * Get users in an enterprise (admin only)
 * @param {string} enterpriseId - Enterprise ID
 * @returns {Promise<Array>} - List of enterprise users
 */
export const getEnterpriseUsers = async (enterpriseId) => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.get(`/api/v1/enterprise/${enterpriseId}/users?userId=${userId}`);
    
    return response.data.users;
  } catch (error) {
    console.error('Error fetching enterprise users:', error);
    throw error;
  }
};

/**
 * Join an enterprise using a key
 * @param {string} enterpriseKey - Enterprise key
 * @returns {Promise<Object>} - Join result
 */
export const joinEnterprise = async (enterpriseKey) => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.post('/api/v1/enterprise/join', {
      userId,
      enterpriseKey
    });
    
    return response.data;
  } catch (error) {
    console.error('Error joining enterprise:', error);
    throw error;
  }
};

/**
 * Revoke a user's enterprise access (admin only)
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userIdToRevoke - User ID to revoke
 * @returns {Promise<Object>} - Revocation result
 */
export const revokeEnterpriseUser = async (enterpriseId, userIdToRevoke) => {
  try {
    const adminUserId = await getCurrentUserId();
    
    const response = await proxyapi.post(`/api/v1/enterprise/${enterpriseId}/revoke`, {
      adminUserId,
      userIdToRevoke
    });
    
    return response.data;
  } catch (error) {
    console.error('Error revoking enterprise user:', error);
    throw error;
  }
};

/**
 * Regenerate enterprise key (admin only)
 * @param {string} enterpriseId - Enterprise ID
 * @returns {Promise<Object>} - New enterprise key
 */
export const regenerateEnterpriseKey = async (enterpriseId) => {
  try {
    const adminUserId = await getCurrentUserId();
    
    const response = await proxyapi.post(`/api/v1/enterprise/${enterpriseId}/regenerate-key`, {
      adminUserId
    });
    
    return response.data;
  } catch (error) {
    console.error('Error regenerating enterprise key:', error);
    throw error;
  }
};

/**
 * Get user's enterprise status
 * @returns {Promise<Object>} - Enterprise status
 */
// In enterprise.js
export const getUserEnterpriseStatus = async () => {
    try {
      const userId = await getCurrentUserId();
      
      // Changed from '/api/v1/users/current/enterprise-status' to use userId directly
      const response = await proxyapi.get(`/api/v1/users/${userId}/enterprise-status`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user enterprise status:', error);
      // Return a default response instead of throwing the error
      return {
        status: 'success',
        isEnterpriseMember: false
      };
    }
  };

  /**
 * Restore a revoked user's enterprise access (admin only)
 * @param {string} enterpriseId - Enterprise ID
 * @param {string} userIdToRestore - User ID to restore
 * @returns {Promise<Object>} - Response with restoration result
 */
export const restoreEnterpriseUser = async (enterpriseId, userIdToRestore) => {
  try {
    const adminUserId = await getCurrentUserId();
    
    const response = await proxyapi.post(`/api/v1/enterprise/${enterpriseId}/restore`, {
      adminUserId,
      userIdToRestore
    });
    
    return response.data;
  } catch (error) {
    console.error('Error restoring enterprise user:', error);
    throw error;
  }
};


/**
 * Check enterprise expiration and trigger status update if needed
 * @param {string} enterpriseId - Enterprise ID
 * @returns {Promise<Object>} - Response with check result
 */
export const checkEnterpriseExpiration = async (enterpriseId) => {
  try {
    const response = await proxyapi.post(`/api/v1/enterprise/${enterpriseId}/check-expiration`);
    return response.data;
  } catch (error) {
    console.error('Error checking enterprise expiration:', error);
    throw error;
  }
};